import { Component, forwardRef, Input, OnInit, Inject, PLATFORM_ID, ElementRef, ViewChild, HostListener } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NG_VALIDATORS, AbstractControl, ValidationErrors, Validator, FormControl } from '@angular/forms';
import { isPlatformBrowser } from '@angular/common';
import { PhoneNumberUtil, PhoneNumberFormat, PhoneNumber } from 'google-libphonenumber';

interface Country {
  name: string;
  code: string;
  dialCode: string;
  flag: string;
  priority?: number;
  areaCodes?: string[];
}

@Component({
  selector: 'app-phone-input',
  templateUrl: './phone-input.html',
  styleUrls: ['./phone-input.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInput),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneInput),
      multi: true
    }
  ]
})
export class PhoneInput implements ControlValueAccessor, Validator, OnInit {
  @ViewChild('phoneInput') phoneInputElement?: ElementRef<HTMLInputElement>;
  @ViewChild('countrySearchInput') countrySearchInput?: ElementRef<HTMLInputElement>;
  
  // Configuration inputs
  @Input() appearance: 'outline' | 'fill' = 'outline';
  @Input() placeholder: string = '';
  @Input() label: string = 'Mobile';
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() defaultCountry: string = 'IN';
  @Input() preferredCountries: string[] = ['IN', 'US', 'GB'];
  @Input() enableSearch: boolean = true;
  @Input() enableAutoFormat: boolean = true;
  @Input() enablePlaceholder: boolean = true;
  @Input() maxLength: number = 15;
  @Input() separateDialCode: boolean = true;
  @Input() numberFormat: PhoneNumberFormat = PhoneNumberFormat.INTERNATIONAL;
  
  // Component state
  countryCode: string = '+91';
  phoneNumber: string = '';
  selectedCountry: Country = { name: 'India', code: 'IN', dialCode: '+91', flag: '🇮🇳', priority: 0 };
  countries: Country[] = [];
  filteredCountries: Country[] = [];
  searchTerm: string = '';
  isDropdownOpen: boolean = false;
  isBrowser: boolean = false;
  isTouched: boolean = false;
  exampleNumber: string = '';

  private phoneUtil: PhoneNumberUtil;
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};
  private failedFlagCodes = new Set<string>();
  private lastValidatedControl: AbstractControl | null = null;

  constructor(
    private hostElement: ElementRef<HTMLElement>,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.phoneUtil = PhoneNumberUtil.getInstance();
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.initializeCountries();
    this.setDefaultCountry();
    this.updatePlaceholder();
  }

  getFlagSrc(countryCode: string): string {
    return `/flags/${countryCode.toLowerCase()}.png`;
  }

  useFlagImage(countryCode: string): boolean {
    return !this.failedFlagCodes.has(countryCode.toLowerCase());
  }

  onFlagError(countryCode: string): void {
    this.failedFlagCodes.add(countryCode.toLowerCase());
  }

  private initializeCountries(): void {
    // Comprehensive list of countries with priorities for preferred countries
    this.countries = [
      { name: 'Afghanistan', code: 'AF', dialCode: '+93', flag: '🇦🇫' },
      { name: 'Albania', code: 'AL', dialCode: '+355', flag: '🇦🇱' },
      { name: 'Algeria', code: 'DZ', dialCode: '+213', flag: '🇩🇿' },
      { name: 'Argentina', code: 'AR', dialCode: '+54', flag: '🇦🇷' },
      { name: 'Australia', code: 'AU', dialCode: '+61', flag: '🇦🇺', priority: 2 },
      { name: 'Austria', code: 'AT', dialCode: '+43', flag: '🇦🇹' },
      { name: 'Bangladesh', code: 'BD', dialCode: '+880', flag: '🇧🇩' },
      { name: 'Belgium', code: 'BE', dialCode: '+32', flag: '🇧🇪' },
      { name: 'Brazil', code: 'BR', dialCode: '+55', flag: '🇧🇷' },
      { name: 'Canada', code: 'CA', dialCode: '+1', flag: '🇨🇦', areaCodes: ['204', '226', '236', '249', '250'] },
      { name: 'China', code: 'CN', dialCode: '+86', flag: '🇨🇳' },
      { name: 'Colombia', code: 'CO', dialCode: '+57', flag: '🇨🇴' },
      { name: 'Denmark', code: 'DK', dialCode: '+45', flag: '🇩🇰' },
      { name: 'Egypt', code: 'EG', dialCode: '+20', flag: '🇪🇬' },
      { name: 'Finland', code: 'FI', dialCode: '+358', flag: '🇫🇮' },
      { name: 'France', code: 'FR', dialCode: '+33', flag: '🇫🇷' },
      { name: 'Germany', code: 'DE', dialCode: '+49', flag: '🇩🇪' },
      { name: 'Ghana', code: 'GH', dialCode: '+233', flag: '🇬🇭' },
      { name: 'Greece', code: 'GR', dialCode: '+30', flag: '🇬🇷' },
      { name: 'Hong Kong', code: 'HK', dialCode: '+852', flag: '🇭🇰' },
      { name: 'India', code: 'IN', dialCode: '+91', flag: '🇮🇳', priority: 0 },
      { name: 'Indonesia', code: 'ID', dialCode: '+62', flag: '🇮🇩' },
      { name: 'Iran', code: 'IR', dialCode: '+98', flag: '🇮🇷' },
      { name: 'Iraq', code: 'IQ', dialCode: '+964', flag: '🇮🇶' },
      { name: 'Ireland', code: 'IE', dialCode: '+353', flag: '🇮🇪' },
      { name: 'Israel', code: 'IL', dialCode: '+972', flag: '🇮🇱' },
      { name: 'Italy', code: 'IT', dialCode: '+39', flag: '🇮🇹' },
      { name: 'Japan', code: 'JP', dialCode: '+81', flag: '🇯🇵' },
      { name: 'Jordan', code: 'JO', dialCode: '+962', flag: '🇯🇴' },
      { name: 'Kenya', code: 'KE', dialCode: '+254', flag: '🇰🇪' },
      { name: 'Kuwait', code: 'KW', dialCode: '+965', flag: '🇰🇼' },
      { name: 'Malaysia', code: 'MY', dialCode: '+60', flag: '🇲🇾' },
      { name: 'Mexico', code: 'MX', dialCode: '+52', flag: '🇲🇽' },
      { name: 'Morocco', code: 'MA', dialCode: '+212', flag: '🇲🇦' },
      { name: 'Nepal', code: 'NP', dialCode: '+977', flag: '🇳🇵' },
      { name: 'Netherlands', code: 'NL', dialCode: '+31', flag: '🇳🇱' },
      { name: 'New Zealand', code: 'NZ', dialCode: '+64', flag: '🇳🇿' },
      { name: 'Nigeria', code: 'NG', dialCode: '+234', flag: '🇳🇬' },
      { name: 'Norway', code: 'NO', dialCode: '+47', flag: '🇳🇴' },
      { name: 'Oman', code: 'OM', dialCode: '+968', flag: '🇴🇲' },
      { name: 'Pakistan', code: 'PK', dialCode: '+92', flag: '🇵🇰' },
      { name: 'Philippines', code: 'PH', dialCode: '+63', flag: '🇵🇭' },
      { name: 'Poland', code: 'PL', dialCode: '+48', flag: '🇵🇱' },
      { name: 'Portugal', code: 'PT', dialCode: '+351', flag: '🇵🇹' },
      { name: 'Qatar', code: 'QA', dialCode: '+974', flag: '🇶🇦' },
      { name: 'Romania', code: 'RO', dialCode: '+40', flag: '🇷🇴' },
      { name: 'Russia', code: 'RU', dialCode: '+7', flag: '🇷🇺', areaCodes: ['3', '4', '8'] },
      { name: 'Saudi Arabia', code: 'SA', dialCode: '+966', flag: '🇸🇦' },
      { name: 'Singapore', code: 'SG', dialCode: '+65', flag: '🇸🇬' },
      { name: 'South Africa', code: 'ZA', dialCode: '+27', flag: '🇿🇦' },
      { name: 'South Korea', code: 'KR', dialCode: '+82', flag: '🇰🇷' },
      { name: 'Spain', code: 'ES', dialCode: '+34', flag: '🇪🇸' },
      { name: 'Sri Lanka', code: 'LK', dialCode: '+94', flag: '🇱🇰' },
      { name: 'Sweden', code: 'SE', dialCode: '+46', flag: '🇸🇪' },
      { name: 'Switzerland', code: 'CH', dialCode: '+41', flag: '🇨🇭' },
      { name: 'Taiwan', code: 'TW', dialCode: '+886', flag: '🇹🇼' },
      { name: 'Thailand', code: 'TH', dialCode: '+66', flag: '🇹🇭' },
      { name: 'Turkey', code: 'TR', dialCode: '+90', flag: '🇹🇷' },
      { name: 'Ukraine', code: 'UA', dialCode: '+380', flag: '🇺🇦' },
      { name: 'United Arab Emirates', code: 'AE', dialCode: '+971', flag: '🇦🇪' },
      { name: 'United Kingdom', code: 'GB', dialCode: '+44', flag: '🇬🇧', priority: 1 },
      { name: 'United States', code: 'US', dialCode: '+1', flag: '🇺🇸', areaCodes: ['201', '202', '203', '205', '206'] },
      { name: 'Vietnam', code: 'VN', dialCode: '+84', flag: '🇻🇳' }
    ];
    
    // Sort by priority and then by name
    this.countries.sort((a, b) => {
      // Preferred countries first
      const aPriority = this.preferredCountries.indexOf(a.code);
      const bPriority = this.preferredCountries.indexOf(b.code);
      
      if (aPriority !== -1 && bPriority !== -1) {
        return aPriority - bPriority;
      }
      if (aPriority !== -1) return -1;
      if (bPriority !== -1) return 1;
      
      // Then by name
      return a.name.localeCompare(b.name);
    });
    
    this.filteredCountries = [...this.countries];
  }

  private setDefaultCountry(): void {
    const defaultCountry = this.countries.find(c => c.code === this.defaultCountry);
    if (defaultCountry) {
      this.selectedCountry = defaultCountry;
      this.countryCode = defaultCountry.dialCode;
    }
  }

  selectCountry(country: Country): void {
    this.selectedCountry = country;
    this.countryCode = country.dialCode;
    this.isDropdownOpen = false;
    this.searchTerm = '';
    this.filteredCountries = [...this.countries];
    this.updatePlaceholder();
    
    // Auto-format existing number for new country
    if (this.phoneNumber && this.enableAutoFormat) {
      this.formatPhoneNumber();
    }
    
    this.updateValue();
    
    // Focus back on input
    if (this.isBrowser && this.phoneInputElement) {
      setTimeout(() => this.phoneInputElement?.nativeElement.focus(), 0);
    }
  }

  toggleDropdown(): void {
    if (!this.disabled) {
      this.isDropdownOpen = !this.isDropdownOpen;
      if (this.isDropdownOpen) {
        this.searchTerm = '';
        this.filteredCountries = [...this.countries];
        if (this.isBrowser) {
          setTimeout(() => this.countrySearchInput?.nativeElement.focus(), 0);
        }
      }
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isBrowser || !this.isDropdownOpen) return;
    const target = event.target as Node | null;
    if (!target) return;
    if (!this.hostElement.nativeElement.contains(target)) {
      this.isDropdownOpen = false;
    }
  }

  filterCountries(): void {
    const search = this.searchTerm.toLowerCase().trim();
    this.filteredCountries = this.countries.filter(country =>
      country.name.toLowerCase().includes(search) ||
      country.dialCode.includes(search) ||
      country.code.toLowerCase().includes(search)
    );
  }

  onPhoneNumberInput(event: any): void {
    // Remove non-numeric characters except + (only at start)
    let value = event.target.value.replace(/[^\d+]/g, '');
    
    // Remove + if not at start
    if (value.indexOf('+') > 0) {
      value = value.replace(/\+/g, '');
    }
    
    // Limit length
    if (value.length > this.maxLength) {
      value = value.substring(0, this.maxLength);
    }
    
    this.phoneNumber = value;
    
    // Auto-format as user types
    if (this.enableAutoFormat && value.length > 2) {
      this.formatPhoneNumber();
    }
    
    this.updateValue();
  }

  onPhoneNumberChange(): void {
    this.updateValue();
  }

  onPhoneNumberPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    
    // Try to parse the pasted number
    try {
      const phoneNumberObj = this.phoneUtil.parse(pastedText, this.selectedCountry.code);
      const countryCode = this.phoneUtil.getRegionCodeForNumber(phoneNumberObj);
      
      // If different country, switch to it
      const country = this.countries.find(c => c.code === countryCode);
      if (country && country.code !== this.selectedCountry.code) {
        this.selectedCountry = country;
        this.countryCode = country.dialCode;
        this.updatePlaceholder();
      }
      
      // Set the national number
      this.phoneNumber = phoneNumberObj.getNationalNumber()?.toString() || '';
      
      if (this.enableAutoFormat) {
        this.formatPhoneNumber();
      }
      
      this.updateValue();
    } catch (e) {
      // If parsing fails, just clean and set the number
      const cleaned = pastedText.replace(/[^\d]/g, '');
      if (cleaned.length <= this.maxLength) {
        this.phoneNumber = cleaned;
        this.updateValue();
      }
    }
  }

  handleBlur(): void {
    this.isTouched = true;
    this.onTouched();
    
    // Final format on blur
    if (this.enableAutoFormat && this.phoneNumber) {
      this.formatPhoneNumber();
    }
  }

  handleFocus(): void {
    // Could be used for additional focus behaviors
  }

  private formatPhoneNumber(): void {
    if (!this.phoneNumber || !this.isBrowser) return;
    
    try {
      const fullNumber = `${this.countryCode}${this.phoneNumber}`;
      const phoneNumberObj = this.phoneUtil.parse(fullNumber, this.selectedCountry.code);
      
      if (this.phoneUtil.isValidNumber(phoneNumberObj)) {
        // Format based on selected format
        const formattedNumber = this.phoneUtil.format(phoneNumberObj, this.numberFormat);
        
        // Extract national number with formatting
        const nationalNumber = formattedNumber.replace(this.countryCode, '').trim();
        this.phoneNumber = nationalNumber.replace(/^\s*/, '');
      }
    } catch (e) {
      // If formatting fails, keep the original number
    }
  }

  private updatePlaceholder(): void {
    if (!this.enablePlaceholder || !this.isBrowser) {
      this.exampleNumber = '';
      return;
    }
    
    try {
      // Get example number for the selected country
      const exampleNumber = this.phoneUtil.getExampleNumberForType(
        this.selectedCountry.code,
        0 // Mobile number type
      );
      
      if (exampleNumber) {
        const formatted = this.phoneUtil.format(exampleNumber, PhoneNumberFormat.NATIONAL);
        this.exampleNumber = formatted;
        
        if (!this.placeholder) {
          this.placeholder = formatted;
        }
      }
    } catch (e) {
      this.exampleNumber = '';
    }
  }

  hasInvalidNumber(): boolean {
    const control = this.lastValidatedControl;
    if (control) {
      const shouldShow = control.touched || control.dirty || this.isTouched;
      return shouldShow && control.hasError('invalidNumber');
    }
    if (!this.phoneNumber || !this.isTouched) return false;
    const fallbackControl = new FormControl(this.phoneNumber);
    const errors = this.validate(fallbackControl);
    return errors?.['invalidNumber'] === true;
  }

  hasRequiredError(): boolean {
    const control = this.lastValidatedControl;
    if (!control) return this.required && !this.phoneNumber && this.isTouched;
    const shouldShow = control.touched || control.dirty || this.isTouched;
    return shouldShow && control.hasError('required');
  }

  shouldShowErrors(): boolean {
    const control = this.lastValidatedControl;
    if (!control) return this.isTouched;
    return control.touched || control.dirty || this.isTouched;
  }

  getFormattedNumber(format: PhoneNumberFormat = PhoneNumberFormat.E164): string {
    if (!this.phoneNumber) return '';
    
    try {
      const fullNumber = `${this.countryCode}${this.phoneNumber}`;
      const phoneNumberObj = this.phoneUtil.parse(fullNumber, this.selectedCountry.code);
      
      if (this.phoneUtil.isValidNumber(phoneNumberObj)) {
        return this.phoneUtil.format(phoneNumberObj, format);
      }
    } catch (e) {
      // Return raw if formatting fails
    }
    
    return `${this.countryCode}${this.phoneNumber}`;
  }

  getNumberType(): string {
    if (!this.phoneNumber) return '';
    
    try {
      const fullNumber = `${this.countryCode}${this.phoneNumber}`;
      const phoneNumberObj = this.phoneUtil.parse(fullNumber, this.selectedCountry.code);
      
      if (this.phoneUtil.isValidNumber(phoneNumberObj)) {
        const type = this.phoneUtil.getNumberType(phoneNumberObj);
        const types: { [key: number]: string } = {
          0: 'Fixed Line',
          1: 'Mobile',
          2: 'Fixed Line or Mobile',
          3: 'Toll Free',
          4: 'Premium Rate',
          5: 'Shared Cost',
          6: 'VoIP',
          7: 'Personal Number',
          8: 'Pager',
          9: 'UAN',
          10: 'Voicemail'
        };
        return types[type] || 'Unknown';
      }
    } catch (e) {
      // Return empty if check fails
    }
    
    return '';
  }

  private updateValue(): void {
    // Return E164 format for the form value
    const fullNumber = this.phoneNumber ? `${this.countryCode}${this.phoneNumber}` : '';
    this.onChange(fullNumber);
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    if (value) {
      // Try to parse the value
      try {
        const phoneNumberObj = this.phoneUtil.parse(value, '');
        const countryCodeStr = this.phoneUtil.getRegionCodeForNumber(phoneNumberObj);
        const country = this.countries.find(c => c.code === countryCodeStr);
        
        if (country) {
          this.selectedCountry = country;
          this.countryCode = country.dialCode;
          this.updatePlaceholder();
        }
        
        // Format the national number
        const nationalNumber = phoneNumberObj.getNationalNumber()?.toString() || '';
        this.phoneNumber = nationalNumber;
        
        if (this.enableAutoFormat && nationalNumber) {
          this.formatPhoneNumber();
        }
      } catch (e) {
        // If parsing fails, try to extract country code and number
        const match = value.match(/^(\+\d+)(\d+)$/);
        if (match) {
          const dialCode = match[1];
          this.phoneNumber = match[2];
          const country = this.countries.find(c => c.dialCode === dialCode);
          if (country) {
            this.selectedCountry = country;
            this.countryCode = dialCode;
            this.updatePlaceholder();
          }
        } else {
          // Last resort: just set as phone number
          this.phoneNumber = value.replace(/[^\d]/g, '');
        }
      }
    } else {
      this.phoneNumber = '';
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Validator implementation
  validate(control: AbstractControl): ValidationErrors | null {
    this.lastValidatedControl = control;
    if (!this.required && !this.phoneNumber) {
      return null;
    }

    if (this.required && !this.phoneNumber) {
      return { required: true };
    }

    if (this.phoneNumber) {
      try {
        const fullNumber = `${this.countryCode}${this.phoneNumber}`;
        const phoneNumberObj = this.phoneUtil.parse(fullNumber, '');
        const isValid = this.phoneUtil.isValidNumber(phoneNumberObj);
        
        if (!isValid) {
          return { invalidNumber: true };
        }
        
        // Additional validation: check if number is possible
        const isPossible = this.phoneUtil.isPossibleNumber(phoneNumberObj);
        if (!isPossible) {
          return { invalidNumber: true };
        }
      } catch (e) {
        return { invalidNumber: true };
      }
    }

    return null;
  }

  // Helper methods for external access
  getCountryCode(): string {
    return this.countryCode;
  }

  getSelectedCountry(): Country {
    return this.selectedCountry;
  }

  resetToDefaultCountry(): void {
    this.setDefaultCountry();
    this.updatePlaceholder();
    this.phoneNumber = '';
    this.updateValue();
  }
}

